import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ref, onValue } from 'firebase/database';
import { database } from '../config/firebase';
import { canOpenLetter, getTimeUntilOpen } from '../utils/moonPhases';
import { useAuth } from '../contexts/AuthContext';
import { useLetterNotification } from '../contexts/LetterNotificationContext';
import { useNavigate } from 'react-router-dom';
import MoonPhaseIcon from '../components/MoonPhaseIcon';
import './PlaceholderPage.css';

const Letters = () => {
  const { userProfile } = useAuth();
  const { markAsRead, isLetterRead } = useLetterNotification();
  const navigate = useNavigate();
  const [letters, setLetters] = useState([]);
  const [openedLetter, setOpenedLetter] = useState(null);
  const [filterView, setFilterView] = useState('received'); // 'received' or 'sent'
  const [openingLetter, setOpeningLetter] = useState(null); // For opening animation
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'unread', 'read', 'sealed'
  const [sortBy, setSortBy] = useState('date'); // 'date', 'status'

  useEffect(() => {
    const lettersRef = ref(database, 'letters/entries');

    const unsubscribe = onValue(lettersRef, (snapshot) => {
      if (snapshot.exists()) {
        const lettersData = snapshot.val();
        const lettersArray = Object.entries(lettersData).map(([id, letter]) => {
          let canOpen = false;

          if (letter.sealMethod === 'moonPhase') {
            canOpen = canOpenLetter(letter.sealedMoonPhase);
          } else if (letter.sealMethod === 'timeDelay') {
            canOpen = new Date() >= new Date(letter.canOpenAt);
          } else if (letter.sealMethod === 'distance') {
            // TODO: Implement distance tracking - for now, always sealed
            canOpen = false;
          } else if (letter.sealMethod === 'weather') {
            // TODO: Implement weather tracking - for now, always sealed
            canOpen = false;
          }

          return {
            id,
            ...letter,
            canOpen,
            timeUntil: letter.sealMethod === 'moonPhase' && !canOpenLetter(letter.sealedMoonPhase)
              ? getTimeUntilOpen(letter.sealedMoonPhase)
              : null,
          };
        });
        // Sort by date, newest first
        lettersArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setLetters(lettersArray);
      } else {
        setLetters([]);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleOpenLetter = (letter) => {
    if (letter.canOpen) {
      setOpeningLetter(letter.id);
      // Delay opening to show animation
      setTimeout(() => {
        setOpenedLetter(letter);
        setOpeningLetter(null);
        // Mark letter as read when opened
        markAsRead(letter.id);
      }, 600);
    }
  };

  const getSealIcon = (letter) => {
    if (letter.sealMethod === 'moonPhase') {
      return <MoonPhaseIcon phase={letter.sealedMoonPhase} size={32} />;
    } else if (letter.sealMethod === 'timeDelay') {
      return <span style={{ fontSize: '24px' }}>⏰</span>;
    } else if (letter.sealMethod === 'distance') {
      return <span style={{ fontSize: '24px' }}>📍</span>;
    } else if (letter.sealMethod === 'weather') {
      return <span style={{ fontSize: '24px' }}>🌤️</span>;
    }
    return <span style={{ fontSize: '24px' }}>🔒</span>;
  };

  const getSealMessage = (letter) => {
    const read = isLetterRead(letter.id);

    if (letter.canOpen) {
      if (read) {
        return <p className="letter-status read">✓ Read</p>;
      }
      return <p className="letter-status open">✨ Open to read</p>;
    }

    if (letter.sealMethod === 'moonPhase') {
      return (
        <div className="letter-status sealed">
          <p>🔒 Sealed until {letter.sealedMoonPhase}</p>
          <p className="days-until">
            Arrives in {letter.timeUntil?.days} day{letter.timeUntil?.days !== 1 ? 's' : ''}
          </p>
          {read && <p className="previously-read">Previously read</p>}
        </div>
      );
    } else if (letter.sealMethod === 'timeDelay') {
      const daysUntil = Math.ceil((new Date(letter.canOpenAt) - new Date()) / (1000 * 60 * 60 * 24));
      return (
        <div className="letter-status sealed">
          <p>🔒 Time-sealed letter</p>
          <p className="days-until">
            Opens in {daysUntil} day{daysUntil !== 1 ? 's' : ''}
          </p>
          {read && <p className="previously-read">Previously read</p>}
        </div>
      );
    } else if (letter.sealMethod === 'distance') {
      return (
        <div className="letter-status sealed">
          <p>🔒 Distance-sealed letter</p>
          <p className="days-until">
            Opens when you're {letter.distanceMiles} miles apart
          </p>
          {read && <p className="previously-read">Previously read</p>}
        </div>
      );
    } else if (letter.sealMethod === 'weather') {
      return (
        <div className="letter-status sealed">
          <p>🔒 Weather-sealed letter</p>
          <p className="days-until">
            Opens when it's {letter.weatherCondition}
          </p>
          {read && <p className="previously-read">Previously read</p>}
        </div>
      );
    }

    return <p className="letter-status sealed">🔒 Sealed</p>;
  };

  // Filter letters based on view (received/sent)
  const viewFilteredLetters = letters.filter(letter => {
    if (filterView === 'sent') {
      return letter.author === userProfile?.name;
    } else {
      return letter.author !== userProfile?.name;
    }
  });

  // Apply status filter
  const statusFilteredLetters = viewFilteredLetters.filter(letter => {
    const read = isLetterRead(letter.id);

    switch (statusFilter) {
      case 'unread':
        return letter.canOpen && !read;
      case 'read':
        return read;
      case 'sealed':
        return !letter.canOpen;
      default:
        return true;
    }
  });

  // Sort filtered letters
  const sortedLetters = [...statusFilteredLetters].sort((a, b) => {
    if (sortBy === 'date') {
      return new Date(b.createdAt) - new Date(a.createdAt);
    } else if (sortBy === 'status') {
      // Sort: unread openable first, then read, then sealed
      const aRead = isLetterRead(a.id);
      const bRead = isLetterRead(b.id);

      if (a.canOpen && !aRead && !(b.canOpen && !bRead)) return -1;
      if (b.canOpen && !bRead && !(a.canOpen && !aRead)) return 1;
      if (a.canOpen && b.canOpen) {
        if (aRead !== bRead) return aRead ? 1 : -1;
      }
      if (a.canOpen !== b.canOpen) return a.canOpen ? -1 : 1;
      return new Date(b.createdAt) - new Date(a.createdAt);
    }
    return 0;
  });

  // Calculate counts for filter badges
  const getFilterCounts = () => {
    const received = letters.filter(l => l.author !== userProfile?.name);
    const sent = letters.filter(l => l.author === userProfile?.name);
    const currentLetters = filterView === 'received' ? received : sent;

    return {
      all: currentLetters.length,
      unread: currentLetters.filter(l => l.canOpen && !isLetterRead(l.id)).length,
      read: currentLetters.filter(l => isLetterRead(l.id)).length,
      sealed: currentLetters.filter(l => !l.canOpen).length,
      received: received.length,
      sent: sent.length
    };
  };

  const counts = getFilterCounts();

  return (
    <div className="placeholder-page parchment-texture">
      {/* Back Arrow Button */}
      <motion.button
        className="letters-back-arrow"
        onClick={() => navigate('/home')}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2, duration: 0.6 }}
        whileHover={{ scale: 1.05, x: -5 }}
        whileTap={{ scale: 0.95 }}
      >
        ← Back
      </motion.button>

      <motion.div
        className="placeholder-content"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <h1>Epistolary</h1>
        <p>Letters sealed with moonlight</p>

        {/* Filter Buttons - Received/Sent */}
        <div className="letters-filter">
          <button
            className={`filter-btn ${filterView === 'received' ? 'active' : ''}`}
            onClick={() => setFilterView('received')}
          >
            📬 Received
            <span className="filter-count">{counts.received}</span>
          </button>
          <button
            className={`filter-btn ${filterView === 'sent' ? 'active' : ''}`}
            onClick={() => setFilterView('sent')}
          >
            📤 Sent
            <span className="filter-count">{counts.sent}</span>
          </button>
        </div>

        {/* Status Filter */}
        <div className="letters-status-filter">
          <button
            className={`status-filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All
            <span className="filter-count">{counts.all}</span>
          </button>
          <button
            className={`status-filter-btn ${statusFilter === 'unread' ? 'active' : ''}`}
            onClick={() => setStatusFilter('unread')}
          >
            Unread
            {counts.unread > 0 && <span className="filter-count unread-count">{counts.unread}</span>}
          </button>
          <button
            className={`status-filter-btn ${statusFilter === 'read' ? 'active' : ''}`}
            onClick={() => setStatusFilter('read')}
          >
            Read
            <span className="filter-count">{counts.read}</span>
          </button>
          <button
            className={`status-filter-btn ${statusFilter === 'sealed' ? 'active' : ''}`}
            onClick={() => setStatusFilter('sealed')}
          >
            Sealed
            <span className="filter-count">{counts.sealed}</span>
          </button>
        </div>

        {/* Sort Options */}
        <div className="letters-sort">
          <label className="sort-label">Sort by:</label>
          <div className="sort-buttons">
            <button
              className={`sort-btn ${sortBy === 'date' ? 'active' : ''}`}
              onClick={() => setSortBy('date')}
            >
              Date
            </button>
            <button
              className={`sort-btn ${sortBy === 'status' ? 'active' : ''}`}
              onClick={() => setSortBy('status')}
            >
              Status
            </button>
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={`${filterView}-${statusFilter}`}
            className="entries-container"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {sortedLetters.length === 0 ? (
              <div className="no-letters-message">
                <div className="placeholder-icon">✉️</div>
                <p>
                  {statusFilter === 'all'
                    ? (filterView === 'sent' ? 'No letters sent yet' : 'No letters received yet')
                    : `No ${statusFilter} letters`}
                </p>
              </div>
            ) : (
              sortedLetters.map((letter, index) => {
                const read = isLetterRead(letter.id);
                return (
                  <motion.div
                    key={letter.id}
                    className={`letter-envelope ${letter.canOpen ? 'can-open' : 'sealed'} ${openingLetter === letter.id ? 'opening' : ''} ${read ? 'is-read' : ''}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={openingLetter === letter.id
                      ? { opacity: 0, scale: 1.1, rotateX: 90 }
                      : { opacity: 1, y: 0, scale: 1, rotateX: 0 }
                    }
                    transition={{
                      delay: openingLetter === letter.id ? 0 : index * 0.1,
                      duration: openingLetter === letter.id ? 0.6 : 0.5
                    }}
                    onClick={() => handleOpenLetter(letter)}
                  >
                    {/* Read indicator badge */}
                    {read && letter.canOpen && (
                      <div className="read-badge">✓</div>
                    )}
                    {/* Unread indicator for openable letters */}
                    {!read && letter.canOpen && (
                      <div className="unread-badge">NEW</div>
                    )}

                    <div className="envelope-seal">
                      {getSealIcon(letter)}
                    </div>
                    <h3 className="letter-from">
                      {filterView === 'sent'
                        ? `To: ${userProfile?.name === 'Omi' ? 'Henry' : 'Omi'}`
                        : `From: ${letter.author}`}
                    </h3>
                    <p className="letter-date">
                      Sent: {new Date(letter.createdAt).toLocaleDateString()}
                    </p>
                    {getSealMessage(letter)}
                  </motion.div>
                );
              })
            )}
          </motion.div>
        </AnimatePresence>

        {/* Letter Reading Modal */}
        <AnimatePresence>
          {openedLetter && (
            <motion.div
              className="letter-modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpenedLetter(null)}
            >
              <motion.div
                className="letter-modal"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                onClick={(e) => e.stopPropagation()}
              >
                <button className="close-letter" onClick={() => setOpenedLetter(null)}>
                  ✕
                </button>
                <div className="letter-date-stamp">
                  {new Date(openedLetter.createdAt).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </div>
                <div className="letter-content-display">
                  <p>{openedLetter.content}</p>
                  <div className="letter-signature">
                    <p>— {openedLetter.signature || openedLetter.author}</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default Letters;
